/**
 * Per-device, per-metric sparkline history for Pro 3EM Main cards.
 * npm test src/utest/energy/emeter_spark_cache.test.ts
 */
import type { EmeterMainMetric } from 'src/helpers/emeter_main_metric';

export const EMETER_SPARK_WINDOW_MS = 120_000;
export const EMETER_SPARK_STALE_MS = 600_000;

export type EmeterSparkSample = {
	value: number;
	at: number;
};

export type EmeterSparkSeries = {
	samples: EmeterSparkSample[];
	updatedAt: number;
};

export type EmeterSparkCache = {
	deviceId: string;
	series: Record<EmeterMainMetric, Record<string, EmeterSparkSeries>>;
};

export type EmeterSparkCardSample = {
	id: string;
	numericValue?: number;
};

const emptySeriesMaps = (): EmeterSparkCache['series'] => ({
	power: {},
	current: {},
});

export const emptyEmeterSparkCache = (deviceId: string): EmeterSparkCache => ({
	deviceId,
	series: emptySeriesMaps(),
});

const pruneWindow = (samples: EmeterSparkSample[], now: number): EmeterSparkSample[] => {
	const cutoff = now - EMETER_SPARK_WINDOW_MS;
	return samples.filter((sample) => sample.at >= cutoff);
};

const isStale = (updatedAt: number, now: number): boolean => now - updatedAt >= EMETER_SPARK_STALE_MS;

export const getEmeterSparkValues = (
	cache: EmeterSparkCache,
	metric: EmeterMainMetric,
	cardId: string,
	now: number,
): number[] | undefined => {
	const series = cache.series[metric][cardId];
	if (!series || isStale(series.updatedAt, now)) return;
	const values = pruneWindow(series.samples, now).map((sample) => sample.value);
	return values.length ? values : undefined;
};

/** One live reading is a drawable flat sparkline; PropertyCard needs at least two points. */
export const toEmeterSparklineValues = (values: number[] | undefined): number[] | undefined => {
	if (!values?.length) return;
	const first = values[0];
	if (values.length === 1 && first != null) return [first, first];
	return values;
};

export const getEmeterSparkline = (
	cache: EmeterSparkCache,
	metric: EmeterMainMetric,
	card: EmeterSparkCardSample,
	now: number,
): number[] | undefined => {
	const cached = getEmeterSparkValues(cache, metric, card.id, now);
	if (cached?.length) return toEmeterSparklineValues(cached);
	if (card.numericValue == null || Number.isNaN(card.numericValue)) return;
	return toEmeterSparklineValues([card.numericValue]);
};

export const pushEmeterSparkSamples = (
	cache: EmeterSparkCache,
	input: {
		deviceId: string;
		metric: EmeterMainMetric;
		cards: EmeterSparkCardSample[];
		now: number;
	},
): EmeterSparkCache => {
	const base = cache.deviceId === input.deviceId ? cache : emptyEmeterSparkCache(input.deviceId);
	const metricSeries = base.series[input.metric];
	let nextMetricSeries: Record<string, EmeterSparkSeries> | undefined;

	for (const card of input.cards) {
		if (card.numericValue == null || Number.isNaN(card.numericValue)) continue;

		const existing = (nextMetricSeries ?? metricSeries)[card.id];
		const stale = !existing || isStale(existing.updatedAt, input.now);
		const lastValue = stale ? undefined : existing.samples[existing.samples.length - 1]?.value;
		if (!stale && lastValue === card.numericValue) continue;

		const samples = pruneWindow(
			[...(stale ? [] : existing.samples), { value: card.numericValue, at: input.now }],
			input.now,
		);
		if (!nextMetricSeries) nextMetricSeries = { ...metricSeries };
		nextMetricSeries[card.id] = { samples, updatedAt: input.now };
	}

	if (!nextMetricSeries && base === cache) return cache;

	return {
		deviceId: input.deviceId,
		series: {
			...base.series,
			[input.metric]: nextMetricSeries ?? metricSeries,
		},
	};
};
